import { defer, firstValueFrom, map } from 'rxjs'
import { tableFromIPC, type Table } from 'apache-arrow'
import { Environment } from 'environment.js'
import { tracedAjax } from '../Sentry.js'
import type { CubeMetadata } from '../../Generated/CubeMetadata.ts'
import type { OtuCubeConfig } from '../../Generated/OtuCubeConfig.ts'
import type { OtuDetail } from '../../Generated/OtuDetail.ts'
import type { OtuScopeInfo } from '../../Generated/OtuScopeInfo.ts'
import type { OtuSequence } from '../../Generated/OtuSequence.ts'
import type { SpinMembers } from '../../Generated/SpinMembers.ts'
import { BlastSource } from '../../Model/Project/BlastSource.js'
import { fetchArrowCube } from './Cubes.js'
import { retryCubeFetch } from './cubeRetry.js'
import { getCachedCubeByKey, putCachedCubeByKey } from './CubeCache.js'

/*
 * The OTU table's data on the visualization pods. Classic (`OtuCubeController`): the per-OTU cube of a scope under one
 * blast source, optionally cut to its `topN` most abundant OTUs, and its `sample × OTU` read matrix. SPIN mode
 * (`SpinSummaryController`): one summary row per SPIN, optionally cut to the `topN` most abundant SPINs, its
 * `SPIN × sample` reads, and a SPIN's member OTUs on demand. The Arrow ones are kept in the cube cache and revalidated
 * by ETag.
 */

let config: Promise<OtuCubeConfig> | null = null

/** The backend's cube settings, fetched once per page load. */
export const fetchOtuCubeConfig = (): Promise<OtuCubeConfig> => {
  config ??= firstValueFrom(
    tracedAjax<OtuCubeConfig>({ url: `${Environment.VIS_URL}/otu/config`, method: 'GET', withCredentials: true }).pipe(
      map((r) => r.response)
    )
  ).catch((e: unknown) => {
    config = null
    throw e
  })
  return config
}

export type LoadedOtuCube = {
  table: Table
  /** OTUs of the scope before the `topN` cut; `null` when the response did not say. */
  otuTotal: number | null
  /** Whether the cut left OTUs out; `null` when the response did not say. */
  truncated: boolean | null
  /** The divisor of the table's representation; `null` when the response did not say. */
  scopeSamples: number | null
  /** The blast sources with data in the scope, in enum order; `null` when the response did not say. */
  sources: BlastSource[] | null
}

/** A SPIN selection: the algorithm, and the As of time ('' = Latest). */
export type SpinChoice = { algorithmType: string; atTime: string }

/** The schema metadata entry the backend writes its `CubeMetadata` under; keep in sync with `CubeMetadata.SCHEMA_KEY`. */
export const CUBE_METADATA_KEY = 'spcfy.meta'

/** The table's {@link CubeMetadata}; all `null` when the response carried none. */
const metadataOf = (table: Table): Pick<LoadedSpinSummary, 'truncated' | 'scopeSamples' | 'sources'> & { total: number | null } => {
  const json = table.schema.metadata.get(CUBE_METADATA_KEY)
  if (json === undefined) {
    return { total: null, truncated: null, scopeSamples: null, sources: null }
  }
  const meta = JSON.parse(json) as CubeMetadata
  const known = new Set<string>(Object.values(BlastSource))
  const sources = (meta.sources ?? []).filter((s) => known.has(s)) as BlastSource[]
  return {
    total: meta.total,
    truncated: meta.truncated,
    scopeSamples: meta.scopeSamples,
    sources: sources.length > 0 ? sources : null
  }
}

type LoadedCube = {
  table: Table
  /** Whether a cached version was there and the server answered with another one. */
  replaced: boolean
}

async function loadCube(key: string, url: string): Promise<LoadedCube> {
  const cached = await getCachedCubeByKey(key)
  let res = await firstValueFrom(defer(() => fetchArrowCube(url, cached?.etag)).pipe(retryCubeFetch()))
  if (res.kind === 'unchanged') {
    if (cached) return { table: tableFromIPC(cached.bytes), replaced: false }
    res = await firstValueFrom(defer(() => fetchArrowCube(url)).pipe(retryCubeFetch()))
    if (res.kind === 'unchanged') throw new Error(`${url} answered 304 to a request without an ETag`)
  }
  const fresh = res
  void putCachedCubeByKey(key, { bytes: fresh.bytes, etag: fresh.etag, loadedAt: Date.now() })
  // Without a cached ETag nothing was revalidated, so a fresh body says nothing about a change.
  const replaced = cached?.etag != null && fresh.etag !== cached.etag
  return { table: fresh.table, replaced }
}

/** The per-OTU cube of [scopeId] under [blastType]: all of it, or the [topN] OTUs with the most reads. */
export async function loadOtuCube(scopeId: string, blastType: BlastSource, topN: number | null): Promise<LoadedOtuCube> {
  const params = new URLSearchParams({ blastType })
  if (topN !== null) params.set('topN', String(topN))
  const { table } = await loadCube(
    `${scopeId}:${blastType}:otu:${topN ?? 'all'}`,
    `${Environment.VIS_URL}/otu/${scopeId}/cube?${params.toString()}`
  )
  const { total, ...rest } = metadataOf(table)
  return { table, otuTotal: total, ...rest }
}

/** `(sample_id, otu_uuid, raw_reads)` for every OTU of [scopeId] in every sample that has it. */
export async function loadOtuReads(scopeId: string): Promise<Table> {
  return (await loadCube(`${scopeId}:otu-reads`, `${Environment.VIS_URL}/otu/${scopeId}/reads`)).table
}

export type LoadedSpinSummary = {
  /** One row per SPIN, as the backend's `SpinSummaryService.SUMMARY_COLUMNS` names them. */
  table: Table
  /** SPINs of the scope before the `topN` cut; `null` when the response did not say. */
  spinTotal: number | null
  /** Whether the cut left SPINs out; `null` when the response did not say. */
  truncated: boolean | null
  /** The divisor of the summary's representation; `null` when the response did not say. */
  scopeSamples: number | null
  /** The blast sources with data in the scope, in enum order; `null` when the response did not say. */
  sources: BlastSource[] | null
  /** Whether this replaced a cached summary of the same selection: the scope's SPINs changed since it was cached. */
  replaced: boolean
}

const spinParams = (spin: SpinChoice, topN: number | null, blastType?: BlastSource): URLSearchParams => {
  const params = new URLSearchParams(blastType ? { blastType } : {})
  params.set('algorithmType', spin.algorithmType)
  if (spin.atTime) params.set('atTime', spin.atTime)
  if (topN !== null) params.set('topN', String(topN))
  return params
}

const spinKey = (spin: SpinChoice, topN: number | null) => `${spin.algorithmType}:${spin.atTime || 'latest'}:${topN ?? 'all'}`

/**
 * One row per current SPIN of [scopeId] under [spin], with each SPIN's attributes under [blastType]: all of them, or
 * the [topN] SPINs with the most reads.
 */
export async function loadSpinSummary(
  scopeId: string,
  blastType: BlastSource,
  spin: SpinChoice,
  topN: number | null
): Promise<LoadedSpinSummary> {
  const { table, replaced } = await loadCube(
    `${scopeId}:${blastType}:spin-summary:${spinKey(spin, topN)}`,
    `${Environment.VIS_URL}/otu/${scopeId}/spins?${spinParams(spin, topN, blastType).toString()}`
  )
  const { total, ...rest } = metadataOf(table)
  return { table, spinTotal: total, ...rest, replaced }
}

/**
 * `(spin_id, sample_id, raw_reads)` for the SPINs {@link loadSpinSummary} returns under the same [spin] and [topN],
 * then per sample a row with a null `spin_id` holding the reads of every OTU of the scope, unclustered ones included.
 */
export async function loadSpinReads(scopeId: string, spin: SpinChoice, topN: number | null): Promise<Table> {
  return (
    await loadCube(
      `${scopeId}:spin-reads:${spinKey(spin, topN)}`,
      `${Environment.VIS_URL}/otu/${scopeId}/spins/reads?${spinParams(spin, topN).toString()}`
    )
  ).table
}

/** The OTUs of [scopeId] whose current SPIN under [spin] is [spinId], most reads first. */
export const fetchSpinMembers = (scopeId: string, spinId: string, spin: SpinChoice): Promise<string[]> =>
  firstValueFrom(
    tracedAjax<SpinMembers>({
      url: `${Environment.VIS_URL}/otu/${scopeId}/spins/${spinId}/members?${spinParams(spin, null).toString()}`,
      method: 'GET',
      withCredentials: true
    }).pipe(map((r) => r.response?.otuUuids ?? []))
  )

/** At most this many OTU ids per details or sequences request, as the backend allows. */
export const OTU_IDS_PER_REQUEST = 500

const postOtuIds = <T>(scopeId: string, path: 'details' | 'sequences', otuUuids: string[]): Promise<T[]> =>
  firstValueFrom(
    tracedAjax<T[]>({
      url: `${Environment.VIS_URL}/otu/${scopeId}/${path}`,
      method: 'POST',
      withCredentials: true,
      headers: { 'Content-Type': 'application/json' },
      body: { otuUuids }
    }).pipe(map((r) => r.response ?? []))
  )

/** What the pipeline stored for up to {@link OTU_IDS_PER_REQUEST} OTUs of [scopeId]; others are left out. */
export const fetchOtuDetails = (scopeId: string, otuUuids: string[]): Promise<OtuDetail[]> =>
  postOtuIds<OtuDetail>(scopeId, 'details', otuUuids)

/** The sequences of up to {@link OTU_IDS_PER_REQUEST} OTUs of [scopeId]. */
export const fetchOtuSequences = (scopeId: string, otuUuids: string[]): Promise<OtuSequence[]> =>
  postOtuIds<OtuSequence>(scopeId, 'sequences', otuUuids)

/** The XLSX's Version Info and Result Info of [scopeId], and its OTUs without a valid blast hit. */
export const fetchOtuScopeInfo = (scopeId: string): Promise<OtuScopeInfo> =>
  firstValueFrom(
    tracedAjax<OtuScopeInfo>({ url: `${Environment.VIS_URL}/otu/${scopeId}/info`, method: 'GET', withCredentials: true }).pipe(
      map((r) => r.response)
    )
  )
